import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance } from '/@/store/session/selectors';
import type { PaginatedResponse } from '/@/types/core';
import type {
    HelpCategory,
    HelpPageDetail,
    HelpPageFeedbackResponse,
    HelpPageListItem,
    ProblemReport,
    ProblemReportCategory,
    ProblemReportCreate,
    ProblemReportMutationArg,
} from './types';

export type {
    HelpCategory,
    HelpPageDetail,
    HelpPageFeedbackResponse,
    HelpPageListItem,
    HelpVideo,
    ProblemReport,
    ProblemReportCreate,
    ProblemReportMutationArg,
} from './types';

const getApiDomain = () => (settings as unknown as { urls: { apiDomain: string } }).urls.apiDomain;

const helpCenterFaqBaseUrl = () => `${getApiDomain()}/core/v1/help-center`;

type HelpCenterPaginated<T> = { count: number; results: T[] };

export const listHelpCenterCategoriesApi = async ({
    appInstanceId,
}: {
    appInstanceId: number;
}): Promise<HelpCategory[]> => {
    const apiUrl = new URL(`${helpCenterFaqBaseUrl()}/categories/`, window.location.origin);
    apiUrl.searchParams.set('app_instance', String(appInstanceId));
    apiUrl.searchParams.set('page_size', '10000');
    const res = await axFetch.get<HelpCenterPaginated<HelpCategory>>(apiUrl.toString());
    return res.data.results;
};

export const listHelpCenterPagesApi = async ({
    appInstanceId,
    categoryId,
}: {
    appInstanceId: number;
    categoryId?: number | null;
}): Promise<HelpPageListItem[]> => {
    const apiUrl = new URL(`${helpCenterFaqBaseUrl()}/pages/`, window.location.origin);
    apiUrl.searchParams.set('app_instance', String(appInstanceId));
    apiUrl.searchParams.set('page_size', '10000');
    if (categoryId != null) {
        apiUrl.searchParams.set('category', String(categoryId));
    }
    const res = await axFetch.get<HelpCenterPaginated<HelpPageListItem>>(apiUrl.toString());
    return res.data.results;
};

export const getHelpCenterPageDetailApi = async ({
    appInstanceId,
    slug,
}: {
    appInstanceId: number;
    slug: string;
}): Promise<HelpPageDetail> => {
    const apiUrl = new URL(`${helpCenterFaqBaseUrl()}/pages/${encodeURIComponent(slug)}/`, window.location.origin);
    apiUrl.searchParams.set('app_instance', String(appInstanceId));
    const res = await axFetch.get<HelpPageDetail>(apiUrl.toString());
    return res.data;
};

export const getHelpPageFeedbackApi = async ({
    appInstanceId,
    slug,
}: {
    appInstanceId: number;
    slug: string;
}): Promise<HelpPageFeedbackResponse> => {
    const apiUrl = new URL(
        `${helpCenterFaqBaseUrl()}/pages/${encodeURIComponent(slug)}/feedback/`,
        window.location.origin
    );
    apiUrl.searchParams.set('app_instance', String(appInstanceId));
    const res = await axFetch.get<HelpPageFeedbackResponse>(apiUrl.toString());
    return res.data;
};

export const postHelpPageFeedbackApi = async ({
    appInstanceId,
    slug,
    useful,
}: {
    appInstanceId: number;
    slug: string;
    useful: boolean;
}): Promise<void> => {
    const apiUrl = new URL(
        `${helpCenterFaqBaseUrl()}/pages/${encodeURIComponent(slug)}/feedback/`,
        window.location.origin
    );
    apiUrl.searchParams.set('app_instance', String(appInstanceId));
    await axFetch.post(apiUrl.toString(), { useful });
};

export const useHelpCenterCategories = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const appInstance = useSelector(getAppInstance);
    const appInstanceId = appInstance?.id;
    const key =
        enabled && appInstanceId != null ? ({ key: 'helpCenterFaqCategories' as const, appInstanceId } as const) : null;
    return useSWRImmutable(key, () => {
        // biome-ignore lint/style/noNonNullAssertion: SWR does not run the fetcher when key is null
        const k = key!;
        return listHelpCenterCategoriesApi({ appInstanceId: k.appInstanceId });
    });
};

export const useHelpCenterPages = ({
    categoryId,
    enabled = true,
}: {
    categoryId?: number | null;
    enabled?: boolean;
} = {}) => {
    const appInstance = useSelector(getAppInstance);
    const appInstanceId = appInstance?.id;
    const categoryKey = categoryId ?? null;
    const key =
        enabled && appInstanceId != null
            ? ({ key: 'helpCenterFaqPages' as const, appInstanceId, categoryId: categoryKey } as const)
            : null;
    return useSWRImmutable(key, () => {
        // biome-ignore lint/style/noNonNullAssertion: SWR does not run the fetcher when key is null
        const k = key!;
        return listHelpCenterPagesApi({ appInstanceId: k.appInstanceId, categoryId: k.categoryId });
    });
};

export const useHelpCenterPageDetail = (slug: string, { enabled = true }: { enabled?: boolean } = {}) => {
    const appInstance = useSelector(getAppInstance);
    const appInstanceId = appInstance?.id;
    const key =
        enabled && appInstanceId != null && Boolean(slug)
            ? ({ key: 'helpCenterFaqPageDetail' as const, appInstanceId, slug } as const)
            : null;
    return useSWRImmutable(key, () => {
        // biome-ignore lint/style/noNonNullAssertion: SWR does not run the fetcher when key is null
        const k = key!;
        return getHelpCenterPageDetailApi({ appInstanceId: k.appInstanceId, slug: k.slug });
    });
};

export function useHelpPageFeedback(appInstanceId: number, slug: string) {
    const feedbackKey = slug ? ({ key: 'helpCenterPageFeedback' as const, appInstanceId, slug } as const) : null;

    const { data, isLoading } = useSWRImmutable(feedbackKey, () => getHelpPageFeedbackApi({ appInstanceId, slug }));

    const { trigger, isMutating } = useSWRMutation(
        feedbackKey,
        async (_key, { arg }: { arg: boolean }) => {
            await postHelpPageFeedbackApi({ appInstanceId, slug, useful: arg });
            return { useful: arg } satisfies HelpPageFeedbackResponse;
        },
        { populateCache: true, revalidate: false }
    );

    const usefulGiven = data?.useful;
    const feedbackGiven = typeof usefulGiven === 'boolean';

    const sendFeedback = useCallback(
        async (useful: boolean) => {
            if (feedbackGiven || isMutating || isLoading) return;
            try {
                await trigger(useful);
            } catch {
                // keep UI usable; user can retry
            }
        },
        [feedbackGiven, isMutating, isLoading, trigger]
    );

    return {
        feedbackGiven,
        useful: typeof usefulGiven === 'boolean' ? usefulGiven : null,
        sendFeedback,
        sending: isMutating,
        loading: isLoading,
    };
}

export const listProblemReportCategoriesApi = async ({
    appInstance,
}: {
    appInstance: number;
}): Promise<PaginatedResponse<ProblemReportCategory>> => {
    const apiUrl = new URL(`${getApiDomain()}/core/v1/help-center/problem-report-categories/`, window.location.origin);
    const queryParams = new URLSearchParams();
    if (appInstance) queryParams.append('app_instance', String(appInstance));
    apiUrl.search = queryParams.toString();
    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useGetProblemReportCategories = () => {
    const appInstance = useSelector(getAppInstance);
    return useSWR(
        {
            key: 'useHelpCenterCategories',
        },
        () => listProblemReportCategoriesApi({ appInstance: appInstance?.id })
    );
};

const createProblemReportApi = async (arg: ProblemReportCreate): Promise<ProblemReport> => {
    const apiUrl = new URL(`${getApiDomain()}/core/v1/help-center/problem-reports/`, window.location.origin);
    const { appInstance, categoryId, description, files } = arg;
    apiUrl.searchParams.set('app_instance', String(appInstance));

    if (files != null && files.length > 0) {
        const formData = new FormData();
        formData.append('category_id', String(categoryId));
        formData.append('description', description);
        for (const file of files) {
            formData.append('files', file);
        }
        const res = await axFetch.post(apiUrl.toString(), formData);
        return res.data;
    }

    const body: Record<string, unknown> = { category_id: categoryId, description };
    const res = await axFetch.post(apiUrl.toString(), body);
    return res.data;
};

export const useCreateProblemReport = () => {
    const appInstance = useSelector(getAppInstance);
    const appInstanceId = appInstance?.id;

    return useSWRMutation(
        ['createProblemReport', appInstanceId ?? null],
        (_key: unknown, { arg }: { arg: ProblemReportMutationArg }) => {
            if (appInstanceId == null) {
                return Promise.reject(new Error('App instance is not available'));
            }
            return createProblemReportApi({ ...arg, appInstance: appInstanceId });
        },
        {
            onSuccess: () => {
                enqueueSnackbar(_('Reporte creado con éxito'), { variant: 'success' });
            },
            onError: () => {
                enqueueSnackbar(_('Error al crear el reporte'), { variant: 'error' });
            },
        }
    );
};
